import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { distinctUntilChanged, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostAccounting } from '../../../../../../models/cost-accounting.model';
import { ICostCenterCostDistribution } from '../../../../../../models/cost-center-cost-distribution.model';
import { ICostCenterCost } from '../../../../../../models/cost-center-cost.model';
import { CostAccountingStateEnum } from '../../../../../../models/enum/cost-accounting-state.enum';
import { ICostCenterCostLayout } from '../../../../../../models/layout/cost-center-cost-layout.model';
import { ICostCenterCostRebookingState } from '../../../../../../services/facades/cost-accounting/cost-center-cost-rebooking-state.model';
import { CostCenterCostRebookingFacade } from '../../../../../../services/facades/cost-accounting/cost-center-cost-rebooking.facade';
import { RebookingLayoutService } from '../../../../../../services/layout/rebooking-layout.service';
import { CostCenterCostHistoryDialogComponent } from '../history-dialog/cost-center-cost-history-dialog.component';
import { RebookingDialogComponent } from '../rebooking-dialog/rebooking-dialog.component';

@Component({
    selector: 'app-bab-rebooking',
    templateUrl: './cost-center-cost.component.html',
    styleUrls: [ './cost-center-cost.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class CostCenterCostComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatSort)
    public sort: MatSort;

    @Input()
    public selectedCostAccounting: ICostAccounting;

    public costAccountingState = CostAccountingStateEnum;

    public dataSource: MatTableDataSource<ICostCenterCost> = new MatTableDataSource<ICostCenterCost>([]);

    public rebookingState$: Observable<ICostCenterCostRebookingState>;
    public layout: ICostCenterCostLayout;
    public isExpandedMap: Record<number, boolean> = {};
    private destroy$: Subject<void> = new Subject();


    constructor(private myRebookingFacade: CostCenterCostRebookingFacade,
                private myRebookingLayoutService: RebookingLayoutService,
                private myMatDialog: MatDialog) {
    }

    public ngOnInit(): void {
        this.layout = this.myRebookingLayoutService.getCostCenterCostLayout();
        this.rebookingState$ = this.myRebookingFacade.subState();

        this.rebookingState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ICostCenterCostRebookingState) => s.costCenterCosts),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: ICostCenterCost[]) => {
                this.dataSource.data = data;
            });

        this.dataSource.sortingDataAccessor = (item: ICostCenterCost, tableColumnName: string): string | number => {
            switch (tableColumnName) {
                case 'costCenterName':
                    return item.costCenter.name;
                default:
                    return item[ tableColumnName ];
            }
        };

        this.dataSource.filterPredicate = (item: ICostCenterCost, filter: string): boolean => {
            return item.costCenter.name.toLowerCase().indexOf(filter.trim().toLowerCase()) !== -1;
        };

        this.myRebookingFacade.setSelectedCostAccounting(this.selectedCostAccounting);
        void this.myRebookingFacade.fetchCostCenterCosts();

    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.myRebookingFacade.clearState();
    }

    public onOpenRebookingDialog(costCenterCostDistribution: ICostCenterCostDistribution): void {

        const dialog: MatDialogRef<RebookingDialogComponent> = this.myMatDialog.open(RebookingDialogComponent, {
            width: '600px',
            data: costCenterCostDistribution,
        });
        dialog.afterClosed().subscribe(() => {
            void this.myRebookingFacade.fetchCostCenterCosts();
        });

    }

    public onCalculateOperationalAccountingSheet(): void {
        void this.myRebookingFacade.calculateOperationalAccountingSheet();
    }

    public applyFilter(filterValue: string): void {
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    public toggleExpand(costCenterCost: ICostCenterCost): void {
        this.isExpandedMap[ costCenterCost.costCenter.id ] = !this.isExpandedMap[ costCenterCost.costCenter.id ];
    }

    public onOpenHistoryDialog(distribution: ICostCenterCostDistribution): void {
        this.myMatDialog.open(CostCenterCostHistoryDialogComponent, {
            width: '40%',
            height: '60%',
            data: distribution,
        });
    }
}
